import type { FC } from 'react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import { BiEvent } from '@wix/da-bi/pkg/events';
import useDuperbrowseLink from '@wix/da-shared-react/pkg/Duperbrowse/hooks/useDuperbrowseLink';
import { getDeviationEntityId } from '@wix/da-shared-react/pkg/redux/normalizr/schemas/deviation';
import { getDeviationUrl } from '@wix/da-shared-react/pkg/DeviationViews/_helpers';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import GhostButton from '@wix/da-ds/pkg/Buttons/GhostButton';
import IconButton from '@wix/da-ds/pkg/Buttons/IconButton/IconButton';
import IconButtonWithBackground from '@wix/da-ds/pkg/Buttons/IconButton/IconButtonWithBackground';
import GoToPageIcon from '@wix/da-ds/pkg/Icons/24x24/GoToPage';

export interface Props {
  deviation: PapiDeviation;
  disableScrollRestoration?: boolean;
  className?: string;
  biData?: BiEvent;
  iconSizeOverride?: IconSize;
  variant?: 'ghost' | 'icon' | 'icon-with-background';
}

const ViewButton: FC<Props> = ({
  deviation,
  disableScrollRestoration,
  className,
  biData,
  iconSizeOverride,
  variant = 'ghost',
}) => {
  const { t } = useTranslation();
  const ref = useRef<HTMLElement | null>(null);
  const onDuperbrowseClick = useDuperbrowseLink(
    getDeviationEntityId(deviation),
    ref,
    undefined,
    disableScrollRestoration
  );

  const commonProps = {
    className,
    biData,
    onClick: onDuperbrowseClick,
    href: getDeviationUrl(deviation),
    target: '_blank',
    innerRef: node => (ref.current = node),
    title: t('common.view'),
  };

  if (variant === 'ghost') {
    return (
      <GhostButton type="button" variant="main" size="medium" {...commonProps}>
        {t('common.view')}
      </GhostButton>
    );
  }

  const IconComponent =
    variant === 'icon-with-background' ? IconButtonWithBackground : IconButton;
  return (
    <IconComponent
      icon={GoToPageIcon}
      iconSizeOverride={iconSizeOverride}
      size="medium"
      aria-label={t('common.view')}
      {...commonProps}
    />
  );
};

ViewButton.displayName = 'ViewButton';
export default ViewButton;
